import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import {
  createEmployerJob,
  employerSignup,
  getEmployerSession,
  type NewJobInput,
} from "@/lib/employer-api";
import { pageHead } from "@/components/site/seo-copy";
import PostRoleView from "@/components/cl/views/PostRoleView";
import { EmployerCountrySelect } from "@/components/site/EmployerCountrySelect";

export const Route = createFileRoute("/post-job")({
  head: () => pageHead("/post-job"),
  component: PostJob,
});

const base =
  "min-height:44px;padding:0 18px;border-radius:999px;font:inherit;font-size:15px;font-weight:600;cursor:pointer;";
const on = base + "background:#0B2160;color:#FFFFFF;border:2px solid #0B2160";
const off = base + "background:#FFFFFF;color:#0B2160;border:1px solid #BDBDB5";

const money = (v: string) => `£${Number(v).toLocaleString("en-GB")}`;

function PostJob() {
  const [setting, setSetting] = useState("Hybrid");
  const [contract, setContract] = useState("Full-time");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const choice = (label: string, current: string, set: (l: string) => void) => ({
    label,
    pressed: label === current ? "true" : "false",
    style: label === current ? on : off,
    pick: () => set(label),
  });

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    const form = e.currentTarget;
    const val = (id: string) =>
      (form.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`)?.value ?? "").trim();

    const baseSalary = val("pr-base");
    const ote = val("pr-ote");
    const note = val("pr-paynote");
    const hasBase = Number(baseSalary) > 0;
    const hasOte = Number(ote) > 0;
    const selectedPayType = val("pr-pay-type");
    const payStructure =
      selectedPayType === "Base + commission"
        ? "Base salary + commission"
        : selectedPayType ||
          (hasBase && hasOte
            ? "Base salary + commission"
            : hasBase
              ? "Base salary only"
              : "Commission only");
    const pay = [
      hasBase ? `${money(baseSalary)} base` : "",
      hasOte ? `OTE ${money(ote)}` : "",
      note,
    ]
      .filter(Boolean)
      .join(", ");

    const section = (label: string, text: string) => (text ? `${label}\n${text}` : "");
    const advert = [
      section("Role type", val("pr-type")),
      val("pr-role"),
      section("Experience needed", val("pr-experience")),
      section("Objectives", val("pr-obj")),
      section("Who we are looking for", val("pr-look")),
      section("What you get", val("pr-get")),
      section("About the company", val("pr-about")),
    ]
      .filter(Boolean)
      .join("\n\n");

    const company = val("pr-co");
    const job: NewJobInput = {
      ref: "",
      title: val("pr-title"),
      seniority: val("pr-experience"),
      contract,
      company,
      location: val("pr-loc"),
      setting,
      payStructure,
      pay: pay || "Not stated",
      advert,
      tags: [],
    };

    setError("");
    setBusy(true);
    try {
      if (!(await getEmployerSession())) {
        await employerSignup({
          company,
          contactName: val("pr-name"),
          email: val("pr-email"),
          phone: val("pr-phone"),
          location: val("pr-country"),
          website: val("pr-web"),
          sector: val("pr-sector"),
        });
      }
      await createEmployerJob(job);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PostRoleView
        v={{
          countrySelect: (
            <EmployerCountrySelect
              id="pr-country"
              required
              style={{ width: "100%", boxSizing: "border-box", minHeight: "52px", border: "1px solid #BDBDB5", borderRadius: "4px", background: "#FFFFFF", color: "#0B2160", font: "inherit", fontSize: "16px", padding: "0 14px" }}
            />
          ),
          settings: ["Remote", "Hybrid", "On-site", "Field"].map((l) =>
            choice(l, setting, setSetting),
          ),
          contracts: ["Full-time", "Part-time", "Contract"].map((l) =>
            choice(l, contract, setContract),
          ),
          done,
          editing: !done,
          submit,
          again: () => setDone(false),
        }}
      />
      {error || busy ? (
        <p
          role="alert"
          style={{
            position: "fixed",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#FFFFFF",
            color: error ? "#B3261E" : "#0B2160",
            border: "1px solid #E3E3DE",
            borderRadius: 8,
            padding: "12px 18px",
            fontFamily: "Archivo, system-ui, sans-serif",
            fontWeight: 700,
            zIndex: 50,
          }}
        >
          {error || "Posting your role…"}
        </p>
      ) : null}
    </>
  );
}
